import {
  Box,
  BoxProps,
  Center,
  CloseButton,
  Collapse,
  Divider,
  HStack,
  Link,
  List,
  ListItem,
  Spinner,
  Text,
  useDisclosure,
  useOutsideClick
} from "@chakra-ui/react"
import { useEffect, useMemo, useRef } from "react"
import { Link as ReactRouterLink } from "react-router-dom"
import { usePreviousDistinct } from "react-use"
import {
  HomeRegistrationFragment,
  useHomeResourceGroupsQuery
} from "../../../api/generated"
import Pathicon from "../../../pathicon/Pathicon"
import { getPathLink, getRegistrationPathLink } from "../../utils"
import GroupAvatars, { GroupName } from "../GroupAvatars"
import ProgressBar from "../ui/ProgressBar"
import SimpleButton, { SimpleButtonProps } from "../ui/SimpleButton"

type SimpleButtonWrapperProps = Omit<SimpleButtonProps, "icon" | "label">

const StartButton = (props: SimpleButtonWrapperProps) => {
  return <SimpleButton label="Start" icon="chevron-right" {...props} />
}

const CompleteButton = (props: SimpleButtonWrapperProps) => {
  return <SimpleButton label="Complete" leftIcon="check" {...props} />
}

const DesignSourceButton = (props: SimpleButtonWrapperProps) => {
  return (
    <SimpleButton label="Source Path" leftIcon="cloud-arrows-fill" {...props} />
  )
}

const ProgressAction = ({
  registration,
  ...buttonProps
}: SimpleButtonWrapperProps & { registration: HomeRegistrationFragment }) => {
  const progress = registration.path?.progress

  return progress ? (
    progress === 1 ? (
      <CompleteButton {...buttonProps} />
    ) : (
      <ProgressBar progress={progress} />
    )
  ) : (
    <StartButton {...buttonProps} />
  )
}

const DesignWithCohortsAction = ({
  registration,
  ...buttonProps
}: SimpleButtonWrapperProps & { registration: HomeRegistrationFragment }) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const { isOpen, onToggle, onClose, getDisclosureProps, getButtonProps } =
    useDisclosure()

  // Close list when clicking outside.
  useOutsideClick({
    ref: containerRef,
    handler: onClose
  })

  const resource = registration.resource!

  // TODO: paginate or load all!
  const { data, loading, refetch } = useHomeResourceGroupsQuery({
    variables: {
      resourceId: resource.id
    },
    skip: !isOpen
  })

  // Refetch groups when the total count changes (which can occur
  // when user creates new cohort).
  const groupsTotal = resource.groups?.total || 0
  const prevGroupsTotal = usePreviousDistinct(groupsTotal)
  useEffect(() => {
    if (prevGroupsTotal && prevGroupsTotal !== groupsTotal) {
      refetch()
    }
  }, [groupsTotal])

  const groups =
    data?.resource?.groups?.edges?.map((edge) => edge?.group!) || []

  if (groupsTotal < 2) {
    return null
  }

  const containerOpenStyleProps: BoxProps = isOpen
    ? {
        w: "100%",
        p: 2,
        backgroundColor: "whiteAlpha.600",
        backdropFilter: "auto",
        backdropBlur: "8px"
      }
    : {}

  return (
    <Box
      ref={containerRef}
      h="100%"
      position="relative"
      overflow="hidden"
      borderRadius={16}
      {...containerOpenStyleProps}
    >
      <SimpleButton
        as={isOpen ? "div" : "button"}
        w={isOpen ? "100%" : "auto"}
        leftIcon="group"
        colorScheme={isOpen ? "blackAlpha" : "whiteAlpha"}
        {...getButtonProps()}
        {...buttonProps}
      >
        <HStack py={1} w="100%">
          <Text as="span" color="inherit" fontSize="sm" fontWeight="bold" m={0}>
            {groupsTotal} Cohorts
          </Text>
          {!isOpen && <Pathicon icon="chevron-down" />}
          {isOpen && !loading && (
            <CloseButton
              color="inherit"
              onClick={(e) => {
                onToggle()
                // Prevent propagating the click event to parent.
                e.stopPropagation()
              }}
              borderRadius="full"
              cursor="pointer"
              ml="auto"
            />
          )}
        </HStack>
      </SimpleButton>

      {isOpen && loading && (
        <Center w="100%" py={6}>
          <Spinner emptyColor="blackAlpha.200" color="blackAlpha.900" />
        </Center>
      )}
      <Collapse in={isOpen} animateOpacity {...getDisclosureProps()}>
        {!!groups.length && (
          <List w="100%" maxH="172px" overflowY="auto" my={0} pl={0} py={1.5}>
            {groups.map((group) => (
              <ListItem
                key={group.id}
                _hover={{
                  backgroundColor: "whiteAlpha.600",
                  textDecoration: "none"
                }}
                h="100%"
                w="100%"
                p={1}
                cursor="pointer"
                borderRadius="lg"
                minH="1em"
              >
                <Link
                  as={ReactRouterLink}
                  to={getPathLink({
                    resourceSlug: registration.resource?.slug!,
                    cohortId: group.id,
                    pathId: group.user_registration?.path_id!,
                    // Force design role when none specified.
                    registrationRole: group.user_registration?.role || 20
                  })}
                  _hover={{ textDecoration: "none" }}
                  w="100%"
                >
                  <GroupAvatars
                    groupId={group.id as number}
                    showGroupName
                    fontColor="blackAlpha.900"
                    fallback={() => (
                      <GroupName color="blackAlpha.900" noOfLines={1}>
                        {group.name}
                      </GroupName>
                    )}
                  />
                </Link>
              </ListItem>
            ))}
          </List>
        )}

        {!!groups.length && <Divider />}
        {/* This isOpen "hack" is to prevent flash of black 'Source Path' button. */}
        {isOpen && (
          <DesignSourceButton
            w="100%"
            colorScheme="blackAlpha"
            {...buttonProps}
          />
        )}
      </Collapse>
    </Box>
  )
}

const PathCardAction = ({
  registration,
  ...buttonProps
}: {
  registration: HomeRegistrationFragment
} & SimpleButtonWrapperProps) => {
  const toPath = useMemo(() => getRegistrationPathLink(registration), [])

  switch (registration.role) {
    case -1:
      return (
        <SimpleButton
          label="Guided Tour"
          icon="group"
          to={toPath}
          {...buttonProps}
        />
      )
    case 5:
    case 10:
      return (
        <ProgressAction
          registration={registration}
          to={toPath}
          {...buttonProps}
        />
      )
    case 15:
      return null
    case 20:
      return registration.group?.is_master ? (
        <DesignWithCohortsAction
          registration={registration}
          to={toPath}
          {...buttonProps}
        />
      ) : registration.resource?.groups?.total! <= 1 ? (
        <DesignSourceButton to={toPath} {...buttonProps} />
      ) : null
  }
}

export default PathCardAction
